/**
 * Inference (model) status helpers, shared by the worklist render and the
 * reader-cohort poll.
 *
 * A study is canonicalized (and therefore viewable + on the worklist) before
 * its AI model finishes running, so an empty findings list is ambiguous: it can
 * mean "the model is still running" or "the model completed and found nothing".
 * These helpers tell the two apart from the device-server job status stamped on
 * the row, so the UI can show a distinct "pending" state instead of a
 * misleading "no findings".
 */

// device-server JobStatus values (src/schemas/device_server.py):
// pending | processing | completed | failed | cancelled. Only the first two are
// non-terminal — i.e. the model hasn't produced its results yet.
export const RUNNING_STATUSES: ReadonlySet<string> = new Set(["pending", "processing"]);

/**
 * True while the model is queued or running. False for terminal states
 * (completed/failed/cancelled) and for missing/unknown status — we never claim
 * "running" without an explicit pending/processing signal.
 */
export function isModelRunning(status: string | null | undefined): boolean {
  return status != null && RUNNING_STATUSES.has(status);
}

// Terminal failure: the model ran but produced no result (e.g. an
// OutOfMemory kill on a large-volume study). Distinct from "completed,
// nothing found" so the worklist shows an explicit error instead of a
// misleading "no findings".
export const FAILED_STATUSES: ReadonlySet<string> = new Set(["failed"]);

export function isModelFailed(status: string | null | undefined): boolean {
  return status != null && FAILED_STATUSES.has(status);
}

/** Structural subset of ReaderStudyItem carrying the per-OP detection lists. */
export interface DetectedFindingsSource {
  detected_at_recommended?: string[];
  detected_at_high_specificity?: string[];
  detected_at_balanced?: string[];
  detected_at_high_sensitivity?: string[];
}

/**
 * The detected findings for a study at the best available operating point.
 * Upload worklist ONLY. This is a silent OP fall-through — CLAUDE.md forbids it
 * for classification / MISS counts (a high_sensitivity fallback ~2x's the set,
 * cf. operatingPoints.ts drafter note). Do not adopt for new surfaces.
 */
export function detectedFindings(study: DetectedFindingsSource | undefined): string[] {
  if (!study) return [];
  return (
    study.detected_at_recommended ??
    study.detected_at_high_specificity ??
    study.detected_at_balanced ??
    study.detected_at_high_sensitivity ??
    []
  );
}
